import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "@phosphor-icons/react";
import SiteNav from "../components/site/SiteNav";
import SiteFooter from "../components/site/SiteFooter";
import { SHOP_CATS, PRODUCTS, SWATCHES, useCart } from "../lib/site";

const Word = ({ dark }) => <span className={`font-extrabold tracking-[-0.04em] ${dark ? "text-[#16294A]" : "text-white"}`}>bsafe</span>;

function ProductArt({ p, colour }) {
  const src = p.imgs?.[colour] || (p.imgs && Object.values(p.imgs)[0]);
  if (src) return (
    <div className="group/art relative h-44 rounded-2xl flex items-center justify-center p-4 overflow-hidden" style={{ background: p.bg }} data-testid={`product-img-${p.id}`}>
      <img src={src} alt={`${p.name} ${colour}`} loading="lazy" className={`max-h-full max-w-full object-contain drop-shadow-md transition-opacity duration-300 ${p.alt ? "group-hover/art:opacity-0" : ""} ${p.cat === "wallet" ? "rounded-xl" : ""}`} />
      {p.alt && <img src={p.alt} alt={`${p.name} back`} loading="lazy" data-testid={`product-back-${p.id}`} className={`absolute inset-0 m-auto max-h-[85%] max-w-[85%] object-contain opacity-0 group-hover/art:opacity-100 transition-opacity duration-300 ${p.cat === "wallet" ? "rounded-xl drop-shadow-md" : ""}`} />}
      {p.alt && <span className="absolute bottom-2 right-3 text-[10px] font-semibold text-slate-500 group-hover/art:opacity-0 transition-opacity">Hover to see back</span>}
    </div>);
  const dark = p.fill === "#D4F02E";
  const base = "flex items-center justify-center shadow-lg";
  const shapes = {
    band: <div className={`${base} w-[70%] h-9 rounded-full text-sm`} style={{ background: p.fill }}><Word /></div>,
    wide: <div className={`${base} w-[62%] h-20 rounded-2xl text-lg`} style={{ background: p.fill }}><Word /></div>,
    thin: <div className={`${base} w-[75%] h-5 rounded-full text-[11px]`} style={{ background: p.fill }}><Word /></div>,
    "thin-sm": <div className={`${base} w-[58%] h-4 rounded-full text-[10px]`} style={{ background: p.fill }}><Word dark /></div>,
    card: <div className={`${base} w-[62%] aspect-[1.58] rounded-xl text-lg`} style={{ background: p.fill }}><Word /></div>,
    square: <div className={`${base} w-[45%] aspect-square rounded-2xl text-base`} style={{ background: p.fill }}><Word /></div>,
    cap: <div className={`${base} w-[52%] h-20 rounded-t-full text-base`} style={{ background: p.fill }}><Word /></div>,
    vest: <div className={`${base} w-[38%] aspect-[0.8] rounded-xl text-sm`} style={{ background: p.fill }}><Word dark={dark} /></div>,
  };
  return <div className="h-40 rounded-2xl flex items-center justify-center" style={{ background: p.bg, fontFamily: "Sora, sans-serif" }}>{shapes[p.shape]}</div>;
}

function ProductCard({ p }) {
  const { add } = useCart();
  const [colour, setColour] = useState(p.colours[0]);
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-3 flex flex-col lift" data-testid={`product-${p.id}`}>
      <ProductArt p={p} colour={colour} />
      <div className="px-1 pt-4 flex-1">
        <div className="text-[10px] font-extrabold tracking-[0.15em] text-[#D7263D]">{p.label}</div>
        <div className="mt-1 font-bold text-[#16294A]">{p.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{p.desc}</div>
        <div className="flex gap-1.5 mt-3" data-testid={`swatches-${p.id}`}>{p.colours.map((k) => [k, SWATCHES[k]]).map(([k, v]) => (
          <button key={k} title={k} onClick={() => setColour(k)} data-testid={`swatch-${p.id}-${k}`}
            className={`w-5 h-5 rounded-full border border-slate-300 transition-transform ${colour === k ? "ring-2 ring-offset-2 ring-[#16294A] scale-110" : ""}`} style={{ background: v }} />))}</div>
      </div>
      <div className="flex items-center justify-between px-1 pt-4 pb-1">
        <span className="font-bold text-sm text-[#16294A]">UGX [0]</span>
        <button data-testid={`add-to-cart-${p.id}`} onClick={() => { add(p.id, colour); toast.success(`${p.name} (${colour}) added to cart`); }}
          className="rounded-full bg-[#16294A] hover:bg-[#D7263D] text-white text-xs font-bold px-4 py-2.5 transition-colors">Add to cart</button>
      </div>
    </div>
  );
}

const Supplier = () => (
  <div className="rounded-3xl border border-dashed border-slate-300 p-10 text-center" data-testid="supplier-placeholder">
    <MapPin size={28} className="mx-auto text-[#D7263D]" />
    <div className="mt-3 font-bold text-[#16294A]">Find a supplier near you</div>
    <p className="text-sm text-slate-500 mt-1">[Supplier list – placeholder: shop name · town · phone]</p>
  </div>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = SHOP_CATS.some(([k]) => k === params.get("c")) ? params.get("c") : "all";
  const shown = cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.cat === cat);
  const title = cat === "all" ? "All products" : SHOP_CATS.find(([k]) => k === cat)[1];
  return (
    <div className="landing bg-white min-h-screen text-[#16294A]" style={{ fontFamily: "Sora, sans-serif" }} data-testid="shop-page">
      <SiteNav active="shop" />
      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        <div className="text-xs text-slate-500" data-testid="shop-breadcrumb"><Link to="/" className="underline">Home</Link> / <Link to="/shop" className="underline">Shop</Link> / {title}</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold">{title}</h1>
        <p className="mt-3 text-sm text-slate-600 max-w-xl">NFC + QR medical ID bands, wallet cards and more. Every tag is linked to your own bsafe profile. <Link to="/products" data-testid="shop-qr-products-link" className="font-semibold text-[#D7263D] underline">See products with QR →</Link></p>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="shop-categories">{SHOP_CATS.map(([k, l]) => (
          <button key={k} onClick={() => setParams(k === "all" ? {} : { c: k })} data-testid={`shop-cat-${k}`}
            className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${cat === k ? "bg-[#16294A] text-white border-[#16294A]" : "border-slate-300 hover:border-[#16294A]"}`}>{l}</button>))}</div>
        <div className="mt-8">
          {cat === "supplier" ? <Supplier /> : shown.length === 0
            ? <div className="rounded-3xl bg-[#F2F4F8] p-10 text-center text-sm text-slate-500" data-testid="shop-empty">No products in this category yet.</div>
            : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{shown.map((p) => <ProductCard key={p.id} p={p} />)}</div>}
        </div>
        <div className="mt-12 rounded-3xl bg-[#16294A] text-white p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div><div className="font-extrabold text-xl">Ordering for a school, company or family?</div><div className="text-sm text-slate-300 mt-1">Set up your group, invite members on WhatsApp and order tags in bulk with one invoice.</div></div>
          <Link to="/sign-up" data-testid="shop-bulk-cta" className="rounded-full bg-[#D7263D] font-bold text-sm px-6 py-3 text-center">Set up your group</Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
